import React, { useContext } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ApplicationContext } from "../../context/GlobalState";
import { getLocationBySlug } from "../../utils";
import { generateLongTailKeywords } from "../../utils/keywordSeo";
import { useLocale } from "../../context/LocaleContext";
import { getLocalizedDisplay } from "../../utils/localizedDisplay";
import { t } from "../../i18n";
import { FaMapMarkerAlt, FaPhone, FaArrowRight } from "react-icons/fa";
import TrustBadges from "../../components/TrustBadges/TrustBadges";
import HreflangTags from "../../components/HreflangTags/HreflangTags";
import { buildLocationSchema } from "../../utils/schemaMarkup";
import gaHero from "../../assets/images/heroes/ga-hero-main.webp";
import ncHero from "../../assets/images/heroes/nc-hero-main.webp";
import ncLocationCharlotte from "../../assets/images/heroes/nc-location-charlotte.jpg";
import "./LocationPage.css";

const BRAND_HEROES = { gso: gaHero, "apex-nc": ncLocationCharlotte, "apex-tn": ncHero };

export default function LocationPage() {
  const { state } = useContext(ApplicationContext);
  const { locationSlug } = useParams();
  const { locale } = useLocale();
  const prefix = locale === 'en' ? '' : `/${locale}`;
  const location = getLocationBySlug(state.taxonomy, locationSlug);
  const services = state.taxonomy.dimensions.find((d) => d.id === "service")?.values || [];
  const brand = state.brand || state.client;
  const brandKey = brand.brandKey || "gso";

  if (!location) {
    return <div className="container section-padding"><h1>Location Not Found</h1></div>;
  }

  const longTailKeywords = generateLongTailKeywords(location.city, location.state, 'orthopedic-surgery');
  const pageTitle = `${brand.name} in ${location.city}, ${location.state} | Orthopedic & Injury Care`;
  const metaDescription = `Visit ${brand.name} in ${location.city}, ${location.state}. Expert car accident doctor, orthopedic surgeon, pain management, and chiropractic care. Free consultation. Call ${brand.phone || state.client.phone}.`;
  const heroImg = BRAND_HEROES[brandKey] || gaHero;
  const baseDomain = brand?.domain || state.client?.domain || 'gaspineortho.com';
  const locationSchema = buildLocationSchema({ brand, location });

  return (
    <div className="location-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={longTailKeywords.join(', ')} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={`https://${baseDomain}/${locationSlug}`} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`https://${baseDomain}/${locationSlug}`} />
      </Helmet>
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale={`/${locationSlug}`} />
      {locationSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(locationSchema) }} />}

      <section
        className="lp-hero"
        style={{
          backgroundImage: `linear-gradient(var(--glass-hero-overlay, rgba(26,54,93,0.72)), var(--glass-hero-overlay, rgba(26,54,93,0.72))), url(${heroImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container">
          <h1>{brand.name} in {location.city}, {location.state}</h1>
          {location.address && (
            <p className="lp-address"><FaMapMarkerAlt /> {location.address}</p>
          )}
          <div className="lp-phones">
            <a href={`tel:${brand.phone || state.client.phone}`} className="lp-phone"><FaPhone /> {brand.phone || state.client.phone}</a>
            {brand.phoneExisting && (
              <a href={`tel:${brand.phoneExisting}`} className="lp-phone lp-phone-existing">Existing Patients: {brand.phoneExisting}</a>
            )}
          </div>
        </div>
      </section>

      <TrustBadges locale={state.locale} />

      <section className="lp-services section-padding">
        <div className="container">
          <h2>{t('location.servicesIn', locale)} {location.city}</h2>
          <div className="lp-services-grid">
            {services.map((s) => (
              <Link key={s.slug} to={`${prefix}/${locationSlug}/${s.slug}`} className="lp-service-card glass-card">
                <h3>{getLocalizedDisplay(s, locale)}</h3>
                <span className="lp-service-link"><FaArrowRight /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
